import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Clock, ShieldCheck, Trash2, Plus, UserCheck, AlertTriangle, Loader2, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingCompliancePage = () => {
  const { isHR } = useAuth();
  return isHR ? <HRView /> : <EmployeeView />;
};

// ---------------- Employee checklist ----------------
const EmployeeView = () => {
  const [status, setStatus] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [openTraining, setOpenTraining] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API_URL}/api/onboarding/my-status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/onboarding/trainings`, { withCredentials: true }),
      ]);
      setStatus(s.data); setTrainings(t.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markTrainingDone = async (id) => {
    try {
      await axios.post(`${API_URL}/api/onboarding/trainings/${id}/complete`, {}, { withCredentials: true });
      toast.success('Training completed'); setOpenTraining(null); load();
    } catch { toast.error('Failed'); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/onboarding/submit`, {}, { withCredentials: true });
      toast.success('Submitted to HR for approval');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  if (!status) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  // Non-gated (existing / approved / exempt) employees have nothing to do here
  if (status.no_employee_record || !status.gated) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="onboarding-complete">
        <Card><CardContent className="py-12 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold">You're all set!</h2>
          <p className="text-muted-foreground mt-1">Your onboarding is complete and your attendance is enabled.</p>
        </CardContent></Card>
      </div>
    );
  }

  const Item = ({ done, label, sub }) => (
    <div className="flex items-start gap-3 py-2">
      {done ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />}
      <div><p className={`text-sm font-medium ${done ? 'line-through text-muted-foreground' : ''}`}>{label}</p>{sub && <p className="text-xs text-muted-foreground">{sub}</p>}</div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="my-onboarding">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> Complete Your Onboarding</h1>
        <p className="text-sm text-muted-foreground">Finish everything below and submit for HR approval. Your attendance stays disabled until HR approves.</p>
      </div>

      {status.deadline_date && (
        <div className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${status.overdue ? 'border-red-300 bg-red-50 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="onboarding-deadline">
          <Clock className="w-4 h-4" />
          {status.overdue ? `Overdue — please complete immediately (was due ${status.deadline_date}).` : `Complete by ${status.deadline_date} (${status.grace_days}-day window).`}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">1. My Profile</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {status.missing_profile.length === 0
            ? <Item done label="Profile details complete" />
            : <>
                <Item done={false} label="Fill in the missing profile fields" sub={`Missing: ${status.missing_profile.join(', ')}`} />
                <Button size="sm" variant="outline" className="mt-2" onClick={() => window.location.assign('/profile')} data-testid="goto-profile-btn">Go to My Profile</Button>
              </>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">2. Required Documents</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {status.required_documents.map(d => <Item key={d} done={!status.missing_documents.includes(d)} label={d} />)}
          {status.missing_documents.length > 0 && (
            <Button size="sm" variant="outline" className="mt-2" onClick={() => window.location.assign('/profile')} data-testid="goto-docs-btn">Upload Documents</Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">3. Compliance Training</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-2">
          {trainings.length === 0 && <p className="text-sm text-muted-foreground">No training assigned.</p>}
          {trainings.map(t => (
            <div key={t.id} className="flex items-center justify-between gap-2 border-b border-border/50 py-2 last:border-0">
              <div className="flex items-center gap-2">
                {t.completed ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <BookOpen className="w-5 h-5 text-muted-foreground" />}
                <span className="text-sm">{t.title}</span>
              </div>
              {!t.completed && <Button size="sm" variant="outline" onClick={() => setOpenTraining(t)} data-testid={`training-open-${t.id}`}>Start</Button>}
            </div>
          ))}
        </CardContent>
      </Card>

      {openTraining && (
        <Card className="border-primary/40">
          <CardHeader><CardTitle className="text-base">{openTraining.title}</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm whitespace-pre-wrap text-muted-foreground mb-4">{openTraining.content || 'Please read and acknowledge this training.'}</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => markTrainingDone(openTraining.id)} data-testid="training-complete-btn">Mark as Complete</Button>
              <Button size="sm" variant="ghost" onClick={() => setOpenTraining(null)}>Close</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="sticky bottom-4">
        {status.profile_submitted && !status.hr_approved ? (
          <div className="rounded-lg border bg-blue-50 border-blue-200 p-4 text-center text-blue-800 text-sm" data-testid="onboarding-awaiting">
            <Clock className="w-5 h-5 mx-auto mb-1" /> Submitted — awaiting HR approval.
          </div>
        ) : (
          <Button className="w-full" size="lg" disabled={!status.all_complete || submitting} onClick={submit} data-testid="onboarding-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {status.all_complete ? 'I\'ve Completed My Profile — Submit to HR' : 'Complete all steps to submit'}
          </Button>
        )}
      </div>
    </div>
  );
};

// ---------------- HR / Admin view ----------------
const HRView = () => {
  const [pending, setPending] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [config, setConfig] = useState(null);
  const [newTraining, setNewTraining] = useState({ title: '', content: '' });
  const [docsText, setDocsText] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, t, c] = await Promise.all([
        axios.get(`${API_URL}/api/onboarding/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/onboarding/trainings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/onboarding/config`, { withCredentials: true }),
      ]);
      setPending(p.data); setTrainings(t.data); setConfig(c.data);
      setDocsText((c.data.required_documents || []).join('\n'));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await axios.post(`${API_URL}/api/onboarding/approve/${id}`, {}, { withCredentials: true }); toast.success('Approved — attendance enabled'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const addTraining = async () => {
    if (!newTraining.title) { toast.error('Title required'); return; }
    try { await axios.post(`${API_URL}/api/onboarding/trainings`, newTraining, { withCredentials: true }); toast.success('Training added'); setNewTraining({ title: '', content: '' }); load(); }
    catch { toast.error('Failed'); }
  };
  const delTraining = async (id) => {
    try { await axios.delete(`${API_URL}/api/onboarding/trainings/${id}`, { withCredentials: true }); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };
  const saveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/onboarding/config`, {
        grace_days: parseInt(config.grace_days),
        required_documents: docsText.split('\n').map(s => s.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success('Settings saved'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-setup">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> Onboarding & Compliance</h1>
        <p className="text-sm text-muted-foreground">Approve new joiners, manage compliance training and onboarding rules.</p>
      </div>
      <Tabs defaultValue="approvals">
        <TabsList>
          <TabsTrigger value="approvals" data-testid="tab-approvals">Pending Approvals ({pending.length})</TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">Compliance Training</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals" className="mt-4 space-y-3">
          {pending.length === 0 ? <Card><CardContent className="py-10 text-center text-muted-foreground">No new joiners awaiting approval.</CardContent></Card> :
            pending.map(e => (
              <Card key={e.employee_id} data-testid={`pending-${e.employee_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{e.name}</p>
                      {e.overdue && <span className="flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</span>}
                      {e.submitted && e.all_complete && <span className="text-xs text-green-600">Ready</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{e.email} · {e.employee_code} · Joined {e.date_of_joining}</p>
                    {!e.all_complete && (
                      <p className="text-xs text-amber-600 mt-1">
                        Pending: {[...e.missing_profile, ...e.missing_documents, ...e.pending_trainings].slice(0, 5).join(', ') || '—'}
                      </p>
                    )}
                  </div>
                  <Button size="sm" onClick={() => approve(e.employee_id)} className="gap-1 shrink-0" data-testid={`approve-${e.employee_id}`}>
                    <UserCheck className="w-4 h-4" /> Approve
                  </Button>
                </CardContent>
              </Card>
            ))}
        </TabsContent>

        <TabsContent value="training" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Add Training Topic</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="Training title (e.g., Code of Conduct)" value={newTraining.title} onChange={e => setNewTraining({ ...newTraining, title: e.target.value })} data-testid="training-title-input" />
              <Textarea placeholder="Training content / instructions the employee must read" value={newTraining.content} onChange={e => setNewTraining({ ...newTraining, content: e.target.value })} rows={4} data-testid="training-content-input" />
              <Button onClick={addTraining} className="gap-2" data-testid="add-training-btn"><Plus className="w-4 h-4" /> Add Training</Button>
            </CardContent>
          </Card>
          <div className="space-y-2">
            {trainings.map(t => (
              <Card key={t.id} data-testid={`training-item-${t.id}`}><CardContent className="p-4 flex items-center justify-between">
                <div><p className="font-medium text-sm">{t.title}</p>{t.content && <p className="text-xs text-muted-foreground line-clamp-1">{t.content}</p>}</div>
                <Button size="sm" variant="ghost" onClick={() => delTraining(t.id)} data-testid={`training-delete-${t.id}`}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
              </CardContent></Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          {config && (
            <Card><CardContent className="p-5 space-y-4 max-w-lg">
              <div className="space-y-2">
                <Label>Grace period (days to complete onboarding)</Label>
                <Input type="number" min="1" value={config.grace_days} onChange={e => setConfig({ ...config, grace_days: e.target.value })} data-testid="grace-days-input" />
                <p className="text-xs text-muted-foreground">If a new joiner doesn't finish within this many days of joining, their attendance stays blocked and HR is alerted.</p>
              </div>
              <div className="space-y-2">
                <Label>Required documents (one per line)</Label>
                <Textarea rows={5} value={docsText} onChange={e => setDocsText(e.target.value)} data-testid="required-docs-input" />
              </div>
              <Button onClick={saveConfig} data-testid="save-onboarding-config">Save Settings</Button>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OnboardingCompliancePage;
